import React, { useEffect, useState, useContext, useRef, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { secureGet, secureDelete } from "../../config/api";
import { PrimaryButton } from "../../components/PrimaryButton";
import { BrandCard } from "../../components/Card";
import OptionsMenu from "../../components/OptionsMenu";
import DeletePopup from "../../components/DeletePopup";
import PageScroller from "../../components/PageScroller";
import ModuleWrapper from "../../components/ModuleWrapper";
import UserContext from "../../Shared/UserContext";
import SnackbarNotification from "../../components/SnackbarNotification";

import LoadingLogo from "../../components/LoadingLogo";
import AddIcon from "../../icons/plusB.svg";
import EditIcon from "../../icons/edit.svg";
import DeleteIcon from "../../icons/trashR.svg";
const PAGE_SIZE = 30; 

const Brands = () => {
  const history = useHistory();
  const { state: { role } } = useContext(UserContext);
  const { t } = useTranslation();
  const topRef = useRef(null);

  const [moduleData, setModuleData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [isDeletePopupVisible, setDeletePopupVisible] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pageNumber, setPageNumber] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [showModule, setShowModule] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const showSnackbar = (message, severity = 'success', manual = false) => {
    setSnackbar({ open: true, message, severity, manual });
  };
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  useEffect(() => {
    setTotalPages(Math.ceil(filteredData.length / pageSize));
  }, [pageSize, filteredData]);


  const fetchData = useCallback(() => {
    const abortController = new AbortController();
    const signal = abortController.signal;
    const showSnackbar = () => {};

    setLoading(true);
    secureGet("/brands", showSnackbar, { signal })
      .then((response) => {
        if (response?.data) {
          setModuleData(response.data);
          setFilteredData(response.data);
          setTotalPages(Math.ceil(response.data.length / pageSize));
        }
      })
      .catch((error) => {
        if (error.name !== "AbortError") {
          console.error("Error fetching data:", error);
        }
      })
      .finally(() => setLoading(false));

    return () => abortController.abort(); 
  }, [pageSize]);

  useEffect(() => {
    const abortFetch = fetchData();
    return () => abortFetch();
  }, [fetchData]);


  const handleAddClick = () => {
    setSelectedBrand(null);
    setShowModule(true);
  };

  const handleEditClick = (brand) => {
    setSelectedBrand(brand);
    setShowModule(true);
  };

  const handleDeleteClick = (brand) => {
    setItemToDelete(brand);
    setDeletePopupVisible(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;

    try {
      await secureDelete(`/brands/delete/${itemToDelete.id}`, showSnackbar);
      showSnackbar(t("confirmation.brandDelete"), "success");

      const updatedData = moduleData.filter((item) => item.id !== itemToDelete.id);
      setModuleData(updatedData);
      setFilteredData(updatedData);
      setTotalPages(Math.ceil(updatedData.length / pageSize));
    } catch (error) {
      setDeletePopupVisible(false);
      console.error("Error deleting item:", error);
    } finally {
      setDeletePopupVisible(false);
    }
  };

  const handleModuleClose = useCallback((action) => {
    if (action === "submit") fetchData();
    setShowModule(false);
    setSelectedBrand(null);
  }, [fetchData]);

  const getOptions = useCallback(
    (brand) => [
      {
        label: t("actions.modify"),
        icon: EditIcon,
        action: () => handleEditClick(brand),
      },
      {
        label: t("actions.delete"),
        icon: DeleteIcon,
        color: "#EF476F",
        action: () => handleDeleteClick(brand),
      },
    ],
    [t]
  );

  const handlePageChange = (page) => {
    if (page >= 0 && page < totalPages) {
      setPageNumber(page);
      topRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPageNumber(0);
  };

  const paginatedData = filteredData.slice(pageNumber * pageSize, (pageNumber + 1) * pageSize);

  return (
    <div ref={topRef}>
      <Helmet>
        <title>Drivnest - {t("titles.brands")}</title>
      </Helmet>

      <div className="flex items-center mb-[15px] justify-between">
        <h1 className="text-[40px] font-semibold">{t("titles.brands")}</h1>
        {(role === "ROLE_ADMIN" || role === "ROLE_SERVICE_CONSULTANT" || role === "ROLE_STOREKEEPER") && (
          <PrimaryButton label={t("primaryButtons.add_brand")} icon={AddIcon} onClick={handleAddClick} />
        )}
      </div>

      <div className="relative">
        {loading && (
          <div className="absolute inset-0 flex justify-center items-center z-10 pt-20">
            <LoadingLogo />
          </div>
        )}

        <div className="mt-6 grid gap-4 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4" style={{ minHeight: "200px" }}>
          {paginatedData.length > 0 ? (
            paginatedData.map((brand) => (
              <BrandCard
                key={brand.id}
                image={brand.image}
                name={brand.name}
                onClick={() => history.push(`/catalog/products`, { brandId: brand.id })}
                optionsMenu={
                  <div onClick={(e) => e.stopPropagation()}>{(role === "ROLE_ADMIN" || role === "ROLE_SERVICE_CONSULTANT" || role === "ROLE_STOREKEEPER")  && <OptionsMenu options={getOptions(brand)} />}</div>
                }
              />
            ))
          ) : (
            <p className="text-gray-600 text-center w-full"></p>
          )}
        </div>
      </div>

      <PageScroller 
        pageNumber={pageNumber} 
        totalPages={totalPages} 
        onPageChange={handlePageChange} 
        pageSize={pageSize} 
        onPageSizeChange={handlePageSizeChange}
     />

      {isDeletePopupVisible && 
        <DeletePopup 
          open={isDeletePopupVisible} 
          onCancel={() => setDeletePopupVisible(false)} 
          onConfirm={handleDeleteConfirm} 
        />}

      {showModule && 
        <ModuleWrapper 
          moduleType="brands" 
          mode={selectedBrand ? "modify" : "add"} 
          onClose={handleModuleClose} 
          onSuccess={(msg) => showSnackbar(msg, 'success')}
          initialValues={selectedBrand || {}} 
        />}

      <SnackbarNotification 
        open={snackbar.open} 
        message={snackbar.message} 
        severity={snackbar.severity} 
        onClose={() => setSnackbar({ ...snackbar, open: false })} 
        manual={snackbar.manual}
      />
      
    </div>
  );
};

export default Brands;
